import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SEOHead from '@/components/SEOHead';
import { organizationSchema, homepageFaqSchema } from '@/data/schemas';
import {
  Globe, Plane, Hotel, Car, FileText, Shield, Compass, Ticket, Train, Bus,
  Wallet, CreditCard, Ship, Stethoscope, Coffee, Users, Zap, Clock,
  Building2, ArrowRight, CheckCircle, Sparkles, Bot, Brain, MessageSquare,
  BarChart3, Cpu, ChevronRight, Smartphone, Trophy, Music } from
'lucide-react';

const LandingPage = () => {
  // Stats data
  const stats = [
  { value: '33+', label: 'Years of Excellence', suffix: 'Since 1993' },
  { value: '24+', label: 'Travel Products', suffix: 'Integrated Platform' },
  { value: '120+', label: 'Countries Served', suffix: 'Global Reach' },
  { value: '18', label: 'India Offices', suffix: 'Pan-India Presence' },
  { value: '9', label: 'International Offices', suffix: 'Worldwide Operations' },
  { value: '24x7', label: 'Global Support', suffix: 'Round the Clock' }];


  // Featured products - 16 products for 4x4 grid
  const featuredProducts = [
    { icon: Plane, name: 'Flights', desc: 'Domestic & International' },
    { icon: Hotel, name: 'Hotels', desc: 'Global Inventory' },
    { icon: FileText, name: 'Visa Services', desc: 'E-Visa & Sticker' },
    { icon: Shield, name: 'Travel Insurance', desc: 'Comprehensive Coverage' },
    { icon: Ship, name: 'Cruise Booking', desc: 'Luxury Cruises' },
    { icon: Car, name: 'Car Rentals', desc: 'Worldwide Service' },
    { icon: Compass, name: 'Activities', desc: 'Experiences & Tours' },
    { icon: Smartphone, name: 'E-sim', desc: 'Global Reach. Best Rates' },
    { icon: Train, name: 'Rail Booking', desc: 'European & Asian Rail' },
    { icon: Bus, name: 'Bus Services', desc: 'Intercity & International' },
    { icon: Coffee, name: 'Airport Lounge', desc: 'Premium Access' },
    { icon: Ticket, name: 'Entry Tickets', desc: 'Museums & Attractions' },
    { icon: Trophy, name: 'Sports Tickets', desc: 'Global Sports Match Tickets' },
    { icon: Music, name: 'Concert Tickets', desc: 'Global Artist Concert Tickets' },
    { icon: Globe, name: 'Fixed Packages', desc: 'Global Reach' },
    { icon: Zap, name: 'Airport Fast Track', desc: 'Priority Services' },
  ];



  // Partnership models - All 7
  const partnerships = [
  { icon: Users, name: 'B2B Travel Partners', desc: 'For Travel Agents & Tour Operators' },
  { icon: Building2, name: 'B2E Enterprise', desc: 'Corporate Travel Solutions' },
  { icon: Cpu, name: 'API Partners', desc: 'Plug into Our Engine' },
  { icon: Globe, name: 'Strategic Partners', desc: 'Shape Global Travel' },
  { icon: Wallet, name: 'Consortium Partners', desc: 'Collective Buying Power' },
  { icon: Compass, name: 'Tourism Ministry', desc: 'Government & DMO Solutions' },
  { icon: CreditCard, name: 'White Label', desc: 'Your Brand, Our Technology' }];


  // AI Features - Osaka AI Products
  const aiFeatures = [
  { icon: MessageSquare, name: 'AI Chat for Flights + Hotels', desc: 'Plan flights, hotels, complex travel via intelligent AI chat' },
  { icon: Bot, name: 'WhatsApp AI', desc: "Book travel via WhatsApp, Email & Instagram with AI" },
  { icon: Brain, name: 'Ask AI for Flights', desc: 'Natural language personalization of flight search results' },
  { icon: Sparkles, name: 'Ask AI for Hotels', desc: 'Personalize hotel sorting with natural language queries' },
  { icon: BarChart3, name: 'Sort by Best', desc: "AI-powered sorting by cost, comfort, intent & personalizations" },
  { icon: Zap, name: 'AI Customer Support', desc: "Handle pre & post booking support enquiries automatically with AI" }];


  return (
    <div className="overflow-hidden">
      <SEOHead
        title="World's First Travel Hypermarket & AI-Powered Super App"
        description="OsakaConnect offers 24+ travel products — flights, hotels, visa, insurance, cruises & more — through one AI-powered platform. Join 10,000+ travel partners worldwide."
        path="/"
        keywords="travel hypermarket, B2B travel portal, travel agent platform, OsakaConnect, travel booking platform India, IATA travel agent, white label travel portal"
        jsonLd={[organizationSchema, homepageFaqSchema]}
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 hero-pattern">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{ backgroundImage: `url('https://images.unsplash.com/photo-1684610529682-553625a1ffed?w=1920&q=80')` }} />

          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Floating elements */}
        <div className="absolute top-1/4 left-10 w-20 h-20 rounded-full bg-[#00C2CB]/10 floating-element blur-xl" />
        <div className="absolute top-1/3 right-20 w-32 h-32 rounded-full bg-[#004E89]/10 floating-element blur-xl" />
        <div className="absolute bottom-1/4 left-1/4 w-24 h-24 rounded-full bg-[#00C2CB]/5 floating-element blur-xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center max-w-4xl mx-auto stagger-children">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <img
                src="/osaka-logo.svg"
                alt="OsakaConnect"
                className="h-20 sm:h-24 lg:h-28 w-auto" />

            </div>

            {/* Badge */}
            <Badge
              className="mb-6 px-4 py-2 bg-[#E5F4F5] text-[#004E89] border-none font-medium"
              data-testid="hero-badge">

              <Sparkles className="w-4 h-4 mr-2" />
              Pioneering Travel Innovation Since 1993
            </Badge>

            {/* Main Headline */}
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-7xl font-bold text-[#0F172A] leading-tight tracking-tight mb-6">
              World's First{' '}
              <span className="text-gradient">Travel Hypermarket</span>
              <br />
              & AI-Powered Super App
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8">
              One unified platform. 24+ travel products. 120+ countries. AI-powered intelligence. 
              Built for B2B agents, enterprises, and global partners.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to="/products">
                <Button
                  size="lg"
                  data-testid="hero-cta-explore"
                  className="bg-[#004E89] hover:bg-[#003d6d] text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:shadow-xl hover:scale-105">

                  Start Exploring
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/signup">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-cta-partner"
                  className="rounded-full px-8 py-6 text-lg font-medium border-[#004E89]/20 text-[#004E89] hover:bg-[#E5F4F5] transition-all duration-300">

                  Become a Partner
                </Button>
              </Link>
              <Link to="/super-app">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-cta-login"
                  className="rounded-full px-8 py-6 text-lg font-medium border-[#00C2CB]/30 text-[#004E89] hover:bg-[#E5F4F5] transition-all duration-300">

                  Partner Login
                </Button>
              </Link>
            </div>

            {/* Tagline */}
            <p className="text-sm text-slate-500 font-medium tracking-wide">One Platform. One Mission. Infinite Possibilities.

            </p>
          </div>
        </div>
      </section>

      {/* Stats Ticker */}
      <section className="bg-[#004E89] py-6 overflow-hidden" data-testid="stats-section">
        <div className="animate-ticker flex items-center gap-16 whitespace-nowrap">
          {[...stats, ...stats].map((stat, index) =>
          <div key={index} className="flex items-center gap-4">
              <span className="text-3xl lg:text-4xl font-outfit font-bold text-white">{stat.value}</span>
              <div className="flex flex-col">
                <span className="text-white/90 font-medium">{stat.label}</span>
                <span className="text-white/60 text-sm">{stat.suffix}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 lg:py-32 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#E5F4F5] text-[#004E89] border-none">24+ Products</Badge>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
              24 Travel Products. Infinite Possibilities.
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Introducing for the first time ever - 24 Travel Products on a Single Unified Platform
            </p>
          </div>

          {/* Products Grid - 4x4 Uniform Layout */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featuredProducts.map((product) => {
              const Icon = product.icon;
              return (
                <div
                  key={product.name}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 hover:border-[#00C2CB]/50 transition-all duration-300 hover:shadow-xl p-5 flex flex-col"
                  data-testid={`product-card-${product.name.toLowerCase().replace(' ', '-')}`}>

                  <div className="product-icon-container w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-[#004E89]" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-[#0F172A] text-base mb-1">
                      {product.name}
                    </h3>
                    <p className="text-slate-500 text-xs">{product.desc}</p>
                  </div>
                  <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-[#E5F4F5] to-transparent rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>);

            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button
                variant="outline"
                data-testid="view-all-products-btn"
                className="rounded-full px-8 py-6 border-[#004E89]/20 text-[#004E89] hover:bg-[#E5F4F5]">

                View All 24 Products
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* AI Section - Dark Mode Island */}
      <section className="ai-section py-20 lg:py-32" data-testid="ai-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#00C2CB]/20 text-[#00C2CB] border-none">
              <Sparkles className="w-4 h-4 mr-2" />
              Powered by Osaka AI
            </Badge>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              11 AI Tools. Infinite Possibilities.
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">Osaka AI is an AI support tool in Osaka Connect platform for travel companies. Automate, personalize, and solve complex problems with intelligent AI tools designed for OTAs, TMCs, DMOs, airlines, and hotels.

            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.name}
                  className="glass-dark rounded-2xl p-6 hover:-translate-y-2 transition-all duration-300"
                  data-testid={`ai-feature-${feature.name.toLowerCase().replace(/\s|\+/g, '-')}`}>

                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00C2CB]/20 to-[#004E89]/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#00C2CB]" />
                  </div>
                  <h3 className="font-outfit font-semibold text-white text-lg mb-2">{feature.name}</h3>
                  <p className="text-slate-400 text-sm">{feature.desc}</p>
                </div>);

            })}
          </div>

          {/* All 11 AI Tools List */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 text-sm mb-4">All 11 Osaka AI Tools Available</p>
            <div className="flex flex-wrap justify-center gap-2">
              {['AI Chat', 'AI Email', 'WhatsApp AI', 'Ask AI (Flights)', 'Ask AI (Hotels)',
              'Voice AI', 'Sort by Best (Flights)', 'Sort by Best (Hotels)',
              'Hotel Q&A Widget', 'AI Customer Support', 'ChatGPT App'].map((tool) =>
              <span
                key={tool}
                className="px-3 py-1 rounded-full bg-slate-800/50 text-slate-400 text-xs border border-slate-700/50">
                {tool}
              </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section className="py-20 lg:py-32 bg-[#F8FAFC]" data-testid="partnerships-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#E5F4F5] text-[#004E89] border-none">Partnership Models</Badge>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
              Seven Powerful Partnership Models
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Designed for every stakeholder in the global travel ecosystem. From travel agents to tourism ministries.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerships.slice(0, 4).map((partnership) => {
              const Icon = partnership.icon;
              return (
                <div
                  key={partnership.name}
                  className="bg-white rounded-3xl p-8 border border-slate-100 hover:border-[#00C2CB]/50 hover:shadow-xl transition-all duration-300 card-hover"
                  data-testid={`partnership-card-${partnership.name.toLowerCase().replace(' ', '-')}`}>

                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#004E89] to-[#00C2CB] flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-outfit font-semibold text-[#0F172A] text-xl mb-2">{partnership.name}</h3>
                  <p className="text-slate-500 text-sm mb-4">{partnership.desc}</p>
                  <Link
                    to="/partnerships"
                    className="text-[#004E89] font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">

                    Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>);

            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 max-w-[75%] lg:max-w-none lg:px-[12.5%] mx-auto">
            {partnerships.slice(4).map((partnership) => {
              const Icon = partnership.icon;
              return (
                <div
                  key={partnership.name}
                  className="bg-white rounded-3xl p-8 border border-slate-100 hover:border-[#00C2CB]/50 hover:shadow-xl transition-all duration-300 card-hover"
                  data-testid={`partnership-card-${partnership.name.toLowerCase().replace(' ', '-')}`}>

                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#004E89] to-[#00C2CB] flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-outfit font-semibold text-[#0F172A] text-xl mb-2">{partnership.name}</h3>
                  <p className="text-slate-500 text-sm mb-4">{partnership.desc}</p>
                  <Link
                    to="/partnerships"
                    className="text-[#004E89] font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">

                    Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>);

            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/partnerships">
              <Button
                data-testid="view-partnerships-btn"
                className="bg-[#004E89] hover:bg-[#003d6d] text-white rounded-full px-8 py-6 font-medium">

                Explore All Partnership Models
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-white border-t border-slate-100" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Trusted & Certified</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
            {['IATA Certified', 'RBI Authorized', 'ISO Certified', 'SSL Secured', 'GDPR Compliant'].map((badge) =>
            <div
              key={badge}
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#F8FAFC] border border-slate-100 trust-badge">

                <CheckCircle className="w-5 h-5 text-[#00C2CB]" />
                <span className="text-slate-700 font-medium">{badge}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-[#004E89] to-[#003d6d]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Travel Business?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join the world's first Travel Hypermarket and unlock access to 24+ travel products, 
            AI-powered tools, and a global network of partners.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup">
              <Button
                size="lg"
                data-testid="cta-signup-btn"
                className="bg-white text-[#004E89] hover:bg-[#E5F4F5] rounded-full px-8 py-6 text-lg font-medium">

                Start Your Partnership
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                data-testid="cta-contact-btn"
                className="rounded-full px-8 py-6 text-lg font-medium border-white/30 text-white hover:bg-white/10">

                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default LandingPage;